import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowRight } from 'lucide-react';

interface EventCardProps {
  id?: string;
  slug?: string;
  title: string;
  date: string;
  location: string;
  distance: string;
  participants: number;
  maxParticipants?: number | null;
  price: string;
  priceSuffix?: string;
  image: string;
  featured?: boolean;
  intensity?: 'low' | 'medium' | 'high' | 'extreme';
  terrain?: 'road' | 'trail' | 'mixed' | 'track';
}

const LEVEL_STYLES = {
  low:     { label: 'FACILE',    bg: '#caff00', fg: '#0a0a0c', glow: 'rgba(202,255,0,0.55)' },
  medium:  { label: 'MODÉRÉ',    bg: '#00e5ff', fg: '#0a0a0c', glow: 'rgba(0,229,255,0.55)' },
  high:    { label: 'DIFFICILE', bg: '#ff7a1a', fg: '#0a0a0c', glow: 'rgba(255,122,26,0.6)' },
  extreme: { label: 'EXTRÊME',   bg: '#ff2d9e', fg: '#ffffff', glow: 'rgba(255,45,158,0.65)' },
} as const;

function TerrainArt({ terrain }: { terrain: EventCardProps['terrain'] }) {
  if (terrain === 'trail') {
    return (
      <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full opacity-55">
        <path d="M0 150 Q50 120 100 140 T200 130 L200 150 Z" fill="#12171a" />
        <path d="M0 150 Q60 135 100 145 T200 140 L200 150 Z" fill="#1a1f22" />
      </svg>
    );
  }
  if (terrain === 'mixed' || terrain === 'track') {
    return (
      <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full opacity-60">
        <g fill="#1a1a22">
          <rect x="20" y="60" width="25" height="90" />
          <rect x="55" y="40" width="20" height="110" />
          <rect x="85" y="70" width="30" height="80" />
          <rect x="125" y="50" width="22" height="100" />
          <rect x="155" y="75" width="28" height="75" />
        </g>
      </svg>
    );
  }
  if (terrain === 'road') {
    return (
      <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full opacity-65">
        <defs>
          <linearGradient id="k-road" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#2c2c36" />
            <stop offset="1" stopColor="#06060a" />
          </linearGradient>
        </defs>
        <rect width="200" height="150" fill="url(#k-road)" />
        <path d="M80 150 L95 70 L105 70 L120 150 Z" fill="#1a1a22" />
        <line x1="100" y1="75" x2="100" y2="150" stroke="#333" strokeDasharray="3 4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full opacity-55">
      <path d="M0 150 L40 90 L70 110 L100 60 L140 100 L200 70 L200 150 Z" fill="#1a1f26" />
    </svg>
  );
}

const EventCard = ({
  id,
  slug,
  title,
  date,
  location,
  distance,
  participants,
  price,
  priceSuffix,
  image,
  featured,
  intensity = 'medium',
  terrain,
}: EventCardProps) => {
  const level = LEVEL_STYLES[intensity];
  const eventUrl = slug ? `/evenement/${slug}` : id ? `/evenement/${id}` : undefined;
  const Wrapper: any = eventUrl ? Link : 'div';
  const wrapperProps = eventUrl ? { to: eventUrl } : {};

  return (
    <Wrapper
      {...wrapperProps}
      className="event-card group relative block bg-card border border-white/10 rounded-[14px] overflow-hidden isolate"
      style={{
        ['--lvl-bg' as any]: level.bg,
        ['--lvl-fg' as any]: level.fg,
        ['--lvl-glow' as any]: level.glow,
      }}
    >
      <span
        aria-hidden
        className="event-card__glow pointer-events-none absolute -inset-px rounded-[16px] opacity-0 z-0 transition-opacity duration-[450ms]"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, var(--lvl-glow), transparent 65%)' }}
      />
      <span
        aria-hidden
        className="event-card__shine pointer-events-none absolute top-0 -left-[60%] h-full w-2/5 z-[3] transition-[left] duration-[700ms] ease-[cubic-bezier(.2,.7,.2,1)]"
        style={{
          background: 'linear-gradient(115deg, transparent 0%, transparent 35%, rgba(255,255,255,0.08) 50%, transparent 65%, transparent 100%)',
          transform: 'skewX(-16deg)',
        }}
      />

      <div className="relative aspect-[4/3] z-[1]">
        <div
          className="event-card__img absolute inset-0 overflow-hidden transition-[filter] duration-500"
          style={{
            background:
              'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.06), transparent 60%), repeating-linear-gradient(135deg, #1a1a22 0 8px, #14141a 8px 16px)',
          }}
        >
          {image ? (
            <img src={image} alt={title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <TerrainArt terrain={terrain} />
          )}
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(10,10,12,0.8))' }}
          />
        </div>

        <div
          className="event-card__km absolute top-0 left-0 z-[2] py-1.5 pl-3 pr-[22px] font-display italic font-extrabold text-base tracking-[0.02em] transition-transform duration-300"
          style={{
            background: '#f8ff3a',
            color: '#0a0a0c',
            clipPath: 'polygon(0 0, 100% 0, 90% 100%, 0 100%)',
          }}
        >
          {distance}
        </div>

        {featured && (
          <div className="absolute top-3 right-3 z-[2] bg-accent text-accent-foreground px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] rounded">
            ★ À la une
          </div>
        )}

        <div
          className="event-card__level absolute left-0 bottom-0 z-[4] inline-flex items-center gap-2 pl-3 pr-5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] origin-left transition-[transform,box-shadow] duration-[350ms] ease-[cubic-bezier(.2,.7,.2,1)]"
          style={{
            background: level.bg,
            color: level.fg,
            clipPath: 'polygon(0 0, 100% 0, 92% 100%, 0 100%)',
            transform: 'translateY(50%)',
          }}
        >
          <span className="h-[5px] w-[5px] rounded-full opacity-85" style={{ background: level.fg }} />
          {level.label}
        </div>
      </div>

      <div className="relative z-[1] px-[18px] pt-6 pb-[18px]">
        <h3 className="event-card__title font-display font-bold uppercase text-[17px] leading-[1.1] tracking-[0.01em] mb-3.5 text-foreground line-clamp-2 transition-colors duration-300">
          {title}
        </h3>

        <div className="flex flex-col gap-1.5 text-[12px] text-muted-foreground mb-3.5">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 opacity-70" />
            <span className="uppercase tracking-wide">{date}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 opacity-70" />
            <span className="line-clamp-1">{location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5 opacity-70" />
            <span>{participants.toLocaleString('fr-FR')} coureurs</span>
          </div>
        </div>

        <div className="pt-3 border-t border-white/10 flex items-end justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground/70">À partir de</div>
            <div className="font-display italic font-extrabold text-[22px] text-primary leading-none tracking-[0.01em]">
              {price}
              {priceSuffix && <span className="text-xs font-sans font-normal not-italic text-muted-foreground ml-1">{priceSuffix}</span>}
            </div>
          </div>
          <div className="event-card__cta h-[34px] w-[34px] rounded-full grid place-items-center bg-secondary border border-white/15 text-foreground transition-[background,color,border-color] duration-200">
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default EventCard;
